/**
 * The artifact panel's geometry: the floors each column needs, the clamp a drag commits through, and the CSS
 * clamp the browser re-resolves at paint time. The JS and CSS forms are deliberately the same formula, so an
 * area that narrows re-clamps the paint for free — no observer, and no monotonic JS re-clamp that would strand
 * the user's chosen width the first time the area narrowed and widened again.
 */

// ~40rem default (assuming the 16px root); bounds are 10%..75% of the chat area the panel docks into.
export const MIN_PANEL_WIDTH_FRACTION = 0.1
export const DEFAULT_PANEL_WIDTH_PX = 640
export const MAX_PANEL_WIDTH_FRACTION = 0.75

/**
 * Narrowest conversation column that still renders. Measured in Chromium at this app's 12px root: below 352px the
 * AI disclaimer wraps to a second line and paints outside its 2.5rem opaque band, over the transcript; below
 * ~300px a fenced code card overflows too. 360px is the measured break rounded up for font variance.
 */
export const CONVERSATION_MIN_WIDTH_PX = 360
/**
 * Narrowest artifact panel that still presents an artifact. Measured the same way: below 366px a table
 * artifact's pagination bar (rows-per-page + page controls) is clipped, and below ~264px the header title
 * truncates to nothing. 384px is that break rounded up.
 */
export const ARTIFACT_MIN_WIDTH_PX = 384
/**
 * The narrowest chat AREA that seats both columns — the sum of the two floors above, and nothing else.
 * Shared so the container query that switches to an overlay and the dock width that avoids needing one cannot drift.
 */
export const SIDE_BY_SIDE_MIN_AREA_PX = CONVERSATION_MIN_WIDTH_PX + ARTIFACT_MIN_WIDTH_PX

/**
 * Clamp a candidate width to its container (the chat area, not the viewport). The ceiling is proportional AND
 * absolute: 75% alone is fine on a 1,900px stage and leaves a 200px conversation on an 800px one, which is worse
 * than the overlay it replaces. Never below the 10% floor, so the bounds cannot invert under the side-by-side
 * threshold, where the panel overlays and this width is not painted at all.
 */
export function clampPanelWidth(width: number, containerWidth: number): number {
  const min = Math.round(containerWidth * MIN_PANEL_WIDTH_FRACTION)
  const proportionalMax = Math.round(containerWidth * MAX_PANEL_WIDTH_FRACTION)
  const max = Math.max(min, Math.min(proportionalMax, containerWidth - CONVERSATION_MIN_WIDTH_PX))
  return Math.min(Math.max(width, min), max)
}

/** The same two-term ceiling as `clampPanelWidth`, expressed for CSS. Both carry it so they cannot drift. */
export function dockedPanelWidthCss(width: number): string {
  const ceiling = `min(${MAX_PANEL_WIDTH_FRACTION * 100}cqi, calc(100cqi - ${CONVERSATION_MIN_WIDTH_PX}px))`
  return `clamp(${MIN_PANEL_WIDTH_FRACTION * 100}cqi, ${width}px, ${ceiling})`
}
